import { descriptionPlainForValidation } from '../../utils/productDescriptionHtml';

export const AI_MATERIAL_MIN_TAGS = 5;
export const AI_MATERIAL_MAX_TAGS = 20;

/** Fabrics / styles commonly mentioned in product copy (beyond quick-select presets). */
const EXTRA_MATERIAL_VOCABULARY = [
  'Khaddar',
  'Karandi',
  'Cambric',
  'Jacquard',
  'Organza',
  'Georgette',
  'Banarsi',
  'Banarasi',
  'Jamawar',
  'Mashru',
  'Muslin',
  'Polyester',
  'Viscose',
  'Rayon',
  'Satin',
  'Crepe',
  'Brocade',
  'Tissue',
  'Wool',
  'Cashmere',
  'Denim',
  'Leather',
  'Suede',
  'Fleece',
  'Jersey',
  'Tulle',
  'Lace',
  'Khadi',
  'Modal',
  'Spandex',
  'Lycra',
  'Nylon',
  'Acrylic',
  'Corduroy',
  'Tweed',
  'Poplin',
  'Voile',
  'Raw Silk',
  'Pure Silk',
  'Cotton Lawn',
  'Cotton Silk',
  'Silk Blend',
  'Embroidered',
  'Printed',
  'Handwoven',
  'Woven',
  'Knitted',
  'Bridal',
  'Party Wear',
  'Semi Formal',
  'Daily Wear',
  'Office Wear',
  'Maxi',
  'Midi',
  'Anarkali',
  'Lehenga',
  'Saree',
  'Sari',
  'Gown',
  'Kaftan',
  'Co-ord',
  'Coord',
  'Two Piece',
  'Three Piece',
  'Single Piece',
  'Pashmina',
  'Karandi',
  'Dori',
  'Zari',
  'Sequins',
  'Mirror Work',
  'Block Print',
  'Digital Print',
  'Screen Print',
  'Tie Dye',
  'Ombre',
  'Floral',
  'Geometric',
  'Checks',
  'Stripes',
  'Solids',
  'Pastel',
  'Bright',
  'Neutral',
  'Breathable',
  'Stretchable',
  'Wrinkle Free',
  'Easy Iron',
  'Machine Wash',
  'Hand Wash',
];

/** Stem in description → canonical tag label */
const STEM_TO_TAG = [
  [/embroid/i, 'Embroidery'],
  [/unstitch/i, 'Unstitched'],
  [/stitch/i, 'Stitched'],
  [/festiv/i, 'Festive'],
  [/formal/i, 'Formal'],
  [/casual/i, 'Casual'],
  [/winter/i, 'Winter'],
  [/summer/i, 'Summer'],
  [/tradition/i, 'Traditional'],
  [/pret\b/i, 'Pret'],
  [/kurti/i, 'Kurti'],
  [/dupatta/i, 'Dupatta'],
  [/trouser/i, 'Trouser'],
  [/shalwar|shalwar\s*kameez|kameez/i, 'Shalwar Kameez'],
  [/abaya/i, 'Abaya'],
  [/chiffon/i, 'Chiffon'],
  [/velvet/i, 'Velvet'],
  [/georgette/i, 'Georgette'],
  [/organza/i, 'Organza'],
  [/khaddar|khadar/i, 'Khaddar'],
  [/karandi/i, 'Karandi'],
  [/cambric/i, 'Cambric'],
  [/jacquard/i, 'Jacquard'],
  [/banaras/i, 'Banarasi'],
  [/jamawar/i, 'Jamawar'],
  [/muslin/i, 'Muslin'],
  [/polyester/i, 'Polyester'],
  [/viscose/i, 'Viscose'],
  [/rayon/i, 'Rayon'],
  [/satin/i, 'Satin'],
  [/crepe/i, 'Crepe'],
  [/brocade/i, 'Brocade'],
  [/linen/i, 'Linen'],
  [/cotton/i, 'Cotton'],
  [/lawn/i, 'Lawn'],
  [/silk/i, 'Silk'],
  [/net\b/i, 'Net'],
  [/chiffon/i, 'Chiffon'],
  [/denim/i, 'Denim'],
  [/wool/i, 'Wool'],
  [/leather/i, 'Leather'],
  [/lace/i, 'Lace'],
  [/bridal|wedding|barat/i, 'Bridal'],
  [/party/i, 'Party Wear'],
  [/anarkali/i, 'Anarkali'],
  [/lehenga/i, 'Lehenga'],
  [/saree|sari/i, 'Saree'],
  [/gown/i, 'Gown'],
  [/print/i, 'Printed'],
  [/hand\s*woven|handwoven/i, 'Handwoven'],
];

const PIECE_PATTERNS = [
  { re: /\b1[\s-]?piece\b|\bone[\s-]?piece\b|\bsingle[\s-]?piece\b/i, label: '1 Piece' },
  { re: /\b2[\s-]?piece\b|\btwo[\s-]?piece\b/i, label: '2 Piece' },
  { re: /\b3[\s-]?piece\b|\bthree[\s-]?piece\b/i, label: '3 Piece' },
];

const LABEL_STOP_WORDS = new Set([
  'the', 'and', 'with', 'for', 'from', 'this', 'that', 'your', 'our', 'is', 'are',
  'was', 'be', 'it', 'in', 'on', 'at', 'to', 'of', 'a', 'an', 'or', 'by', 'as',
  'has', 'have', 'made', 'using', 'quality', 'premium', 'best', 'soft', 'lightweight',
  'comfortable', 'perfect', 'ideal', 'beautiful', 'elegant', 'stylish', 'design',
  'set', 'wear', 'collection', 'new', 'latest', 'product', 'available', 'order',
  'price', 'size', 'color', 'colour', 'shop', 'buy', 'online', 'store', 'sheen',
  'includes', 'comes', 'feature', 'features', 'offer', 'offers', 'high', 'very',
  'all', 'any', 'can', 'will', 'you', 'we', 'us', 'also', 'may', 'not', 'no',
]);

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function toTitleCase(value) {
  return String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function normalizeLabel(value) {
  const cleaned = String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–—:]+|[\s\-–—:.]+$/g, '')
    .trim();
  if (!cleaned || cleaned.length < 2 || cleaned.length > 48) return '';
  const words = cleaned.toLowerCase().split(/\s+/);
  if (words.every((w) => LABEL_STOP_WORDS.has(w))) return '';
  if (words.length === 1 && LABEL_STOP_WORDS.has(words[0])) return '';
  return toTitleCase(cleaned);
}

function addSuggestion(map, rawLabel, score) {
  const label = normalizeLabel(rawLabel);
  if (!label) return;
  const key = label.toLowerCase();
  const prev = map.get(key);
  if (!prev || score > prev.score) {
    map.set(key, { label, score });
  } else {
    prev.score += Math.max(1, Math.floor(score / 4));
  }
}

function buildVocabulary(quickTags = []) {
  const merged = new Set();
  for (const item of [...quickTags, ...EXTRA_MATERIAL_VOCABULARY]) {
    const v = String(item || '').trim();
    if (v) merged.add(v);
  }
  return [...merged].sort((a, b) => b.length - a.length);
}

function buildSingleWordSet(vocabulary) {
  const set = new Set();
  for (const term of vocabulary) {
    if (!term.includes(' ')) {
      set.add(term.toLowerCase());
    }
  }
  return set;
}

function tokenizePlain(plain) {
  return (plain.toLowerCase().match(/[a-z0-9]+(?:'[a-z]+)?/g) || []).filter(
    (w) => w.length >= 3 && !LABEL_STOP_WORDS.has(w)
  );
}

function flexibleTagInText(tag, plainLower) {
  const t = String(tag).toLowerCase().trim();
  if (!t) return false;
  if (plainLower.includes(t)) return true;
  const compact = t.replace(/\s+/g, '');
  const plainCompact = plainLower.replace(/\s+/g, '');
  if (plainCompact.includes(compact)) return true;
  const hyphenated = t.replace(/\s+/g, '-');
  if (plainLower.includes(hyphenated)) return true;
  return false;
}

function extractFromListSegments(plain, suggestions) {
  const segments = plain.split(/[,;|•·\n/]+|(?:\s+and\s+)/i);
  for (const segment of segments) {
    const trimmed = segment.trim();
    if (trimmed.length < 3 || trimmed.length > 80) continue;
    addSuggestion(suggestions, trimmed.replace(/\d{1,3}%?\s*/g, ''), 4);
    const words = tokenizePlain(trimmed);
    if (words.length >= 2 && words.length <= 4) {
      addSuggestion(suggestions, words.join(' '), 3);
    }
  }
}

function extractNgrams(plain, vocabulary, suggestions) {
  const lower = plain.toLowerCase();
  const words = lower.match(/[a-z0-9]+(?:'[a-z]+)?/g) || [];
  for (let size = 3; size >= 1; size -= 1) {
    for (let i = 0; i <= words.length - size; i += 1) {
      const phrase = words.slice(i, i + size).join(' ');
      const match = vocabulary.find((term) => term.toLowerCase() === phrase);
      if (match) addSuggestion(suggestions, match, 7 - size);
    }
  }
}

function rankAndLimit(suggestions, max = AI_MATERIAL_MAX_TAGS) {
  return [...suggestions.values()]
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
    .map((item) => item.label)
    .slice(0, max);
}

/**
 * Extract material / fabric / style tags from product description text.
 * Returns up to AI_MATERIAL_MAX_TAGS; tries to reach AI_MATERIAL_MIN_TAGS when description has enough text.
 */
export function extractMaterialTagsFromDescription(description, quickTags = []) {
  const plain = descriptionPlainForValidation(description);
  if (!plain) return [];

  const suggestions = new Map();
  const vocabulary = buildVocabulary(quickTags);
  const singleWordSet = buildSingleWordSet(vocabulary);
  const plainLower = plain.toLowerCase();

  // 1. Exact vocabulary (longest phrases first)
  for (const term of vocabulary) {
    const re = new RegExp(`\\b${escapeRegex(term)}\\b`, 'i');
    if (re.test(plain)) {
      addSuggestion(suggestions, term, 12);
    }
  }

  // 2. Flexible includes for multi-word quick tags
  for (const tag of quickTags) {
    if (flexibleTagInText(tag, plainLower)) {
      addSuggestion(suggestions, tag, 10);
    }
  }

  // 3. Piece count patterns
  for (const { re, label } of PIECE_PATTERNS) {
    if (re.test(plain)) addSuggestion(suggestions, label, 11);
  }

  // 4. Label patterns (Material: Cotton, Fabric: Silk, etc.)
  const labelPatterns = [
    /(?:material|fabric|composition|textile|blend|made\s+of|crafted\s+from|consists\s+of)[:\s\-–]+([^.,;\n]+)/gi,
    /(?:\b(?:is|are)\s+(?:made\s+of|crafted\s+from|composed\s+of))\s+([^.,;\n]+)/gi,
  ];
  for (const pattern of labelPatterns) {
    let match;
    while ((match = pattern.exec(plain)) !== null) {
      match[1].split(/[,/&+]|(?:\s+and\s+)/i).forEach((part) => {
        addSuggestion(suggestions, part.replace(/\d{1,3}%?\s*/g, ''), 9);
      });
    }
  }

  // 5. Percentage blends (100% cotton)
  const percentPattern = /\b(\d{1,3})%\s*([a-zA-Z][a-zA-Z\s\-]{1,28})/gi;
  let percentMatch;
  while ((percentMatch = percentPattern.exec(plain)) !== null) {
    addSuggestion(suggestions, percentMatch[2], 10);
  }

  // 6. Single-word token scan against material dictionary
  for (const word of tokenizePlain(plain)) {
    if (singleWordSet.has(word)) {
      const canonical = vocabulary.find((t) => t.toLowerCase() === word);
      addSuggestion(suggestions, canonical || word, 8);
    }
  }

  // 7. N-gram phrase scan
  extractNgrams(plain, vocabulary, suggestions);

  // 8. Stem / keyword inference
  for (const [stemRe, label] of STEM_TO_TAG) {
    if (stemRe.test(plain)) addSuggestion(suggestions, label, 6);
  }

  // 9. Comma / list segments
  extractFromListSegments(plain, suggestions);

  // 10. If still below minimum, broaden with vocabulary partial matches
  let results = rankAndLimit(suggestions);
  if (results.length < AI_MATERIAL_MIN_TAGS && plain.length >= 20) {
    for (const term of vocabulary) {
      if (results.length >= AI_MATERIAL_MIN_TAGS) break;
      const key = term.toLowerCase();
      if (results.some((r) => r.toLowerCase() === key)) continue;
      const parts = key.split(/\s+/);
      const anyPartInText = parts.some(
        (p) => p.length >= 4 && plainLower.includes(p)
      );
      if (anyPartInText || flexibleTagInText(term, plainLower)) {
        addSuggestion(suggestions, term, 4);
      }
    }
    for (const tag of quickTags) {
      if (results.length >= AI_MATERIAL_MIN_TAGS) break;
      const words = tag.toLowerCase().split(/\s+/);
      const hit = words.some((w) => w.length >= 4 && plainLower.includes(w));
      if (hit) addSuggestion(suggestions, tag, 3);
    }
    results = rankAndLimit(suggestions);
  }

  return results.slice(0, AI_MATERIAL_MAX_TAGS);
}
